// /src/data/books/DarkForest/data/cap1.js
// Capítulo 1 — Prólogo (versión para LIBRO, texto ampliado, con negritas según lo necesario)
const CAP1 = {
  id: 1,
  slug: "cap1-prologo",
  title: "Capítulo 1 — Prólogo",
  sections: [
    {
      heading: "Bienvenida",
      text: [
        "**¡Enhorabuena por llegar a este libro!** No importa si ya llevas tiempo en el ecosistema cripto o si apenas estás escuchando por primera vez palabras como **wallet, seed phrase o smart contract**.",
        "El simple hecho de abrir este prólogo significa que te estás **tomando en serio tu seguridad y tu aprendizaje** en el mundo digital.",
        "Aquí **no buscamos asustarte**, sino darte un **mapa confiable**. En este entorno, la **falta de información cuesta caro**, y el exceso de ruido también confunde. Vamos a **ordenar el caos**: qué es lo esencial, por qué importa y cómo aprovechar al máximo los capítulos que siguen.",
        "El ‘**bosque oscuro**’ del blockchain existe, pero puedes recorrerlo con **linterna, brújula y un buen plan**."
      ]
    },
    {
      heading: "De qué va este libro",
      text: [
        "El objetivo central es ayudarte a **pensar blockchain como infraestructura de confianza verificable**, y no solo como especulación o inversión.",
        "Exploraremos conceptos técnicos como **bloques, hashes, firmas y consenso**, pero siempre conectados con **decisiones prácticas y reales**.",
        "No usamos contenido protegido ni citas ajenas: este libro está **escrito desde cero con lenguaje claro**, para que puedas **reutilizar fragmentos en tus notas, proyectos o cursos**.",
        "Este libro no te da respuestas rígidas, sino **criterio y método para evaluar riesgos y tomar decisiones**."
      ],
      bluepoints: [
        "🧩 Verás la blockchain como **estructura de confianza**, no solo como precio.",
        "🛡️ Conectarás tecnología con **seguridad de tus activos y decisiones**.",
        "📚 Tendrás contenido **libre, reutilizable y explicable a otros**."
      ]
    },
    {
      heading: "¿Qué aprenderás?",
      text: [
        "Este libro está diseñado para construir tu seguridad digital paso a paso. No tendrás que memorizar todo: **aprenderás lo esencial y sabrás cómo aplicarlo**."
      ],
      list: [
        "Comprender por qué blockchain cambia la conversación sobre **confianza, identidad y custodia**.",
        "Reconocer las piezas mínimas: **billeteras, frases mnemotécnicas, claves, transacciones y redes**.",
        "Identificar **amenazas reales y malas prácticas frecuentes** en el entorno descentralizado.",
        "**Adoptar hábitos de seguridad sostenibles** para proteger tu identidad y patrimonio digital.",
        "Diseñar una **estrategia personal de custodia**: qué mantener en caliente, qué en frío, qué validar primero."
      ],
      bluepoints: [
        "🎯 Aprenderás el **por qué** y no solo el **cómo**.",
        "🧠 Formarás **criterio independiente**, más allá del ruido del mercado.",
        "🔐 Saldrás con una **estrategia aplicable a tu vida digital real**."
      ]
    },
    {
      heading: "¿Cómo leer este libro?",
      text: [
        "**No necesitas ser experta/o**. Lo que sí necesitas es **curiosidad, calma y voluntad de hacer pausas** cuando aparezca un concepto nuevo.",
        "No se trata de leer rápido, sino de **entender con claridad**. Releer está permitido (y recomendado)."
      ],
      list: [
        "**Paso 1 – Lectura completa:** absorber la idea general sin obsesionarse con detalles.",
        "**Paso 2 – Repaso de conceptos clave:** subrayar, copiar y explicar con tus palabras.",
        "**Paso 3 – Acción mínima:** crear una wallet de prueba, simular un respaldo, revisar una firma antes de ejecutarla.",
        "**Paso 4 – Ajuste personal:** adaptar todo a tu realidad, nivel de riesgo y herramientas disponibles."
      ]
    },
    {
      heading: "Mentalidad: del entusiasmo a la prudencia",
      text: [
        "El ecosistema cripto es **rápido, ruidoso y muchas veces exagerado**. Hoy algo parece revolucionario, mañana desaparece.",
        "Aquí trabajaremos con una mentalidad equilibrada: **ni ingenuidad, ni paranoia**, sino **curiosidad responsable**."
      ],
      bluepoints: [
        "🔹 **Entusiasmo informado**, no entusiasmo impulsivo.",
        "🔹 Antes de **confiar**, validar. Antes de **firmar**, entender.",
        "🔹 Antes de mover valor, **ensayar con poco**.",
        "🔹 La **disciplina constante supera cualquier truco puntual**."
      ]
    },
    {
      heading: "Dos principios rectores",
      text: [
        "Si alguna vez dudas sobre qué decisión tomar, vuelve a estos dos principios. Son tu brújula en la dark forest."
      ],
      list: [
        "**Confianza mínima (Zero Trust):** nadie recibe confianza total sin pruebas suficientes (código, reputación, auditorías, permisos claros).",
        "**Validación continua:** lo que ayer era seguro, hoy puede no serlo. **Verificamos, actualizamos y revocamos permisos** regularmente."
      ],
      note: "**Menos confianza implícita. Más verificación explícita.**"
    },
    {
      heading: "Errores frecuentes que vamos a desactivar",
      text: [
        "Muchos desastres en cripto no vienen de hackers geniales, sino de **descuidos normales, repetidos y subestimados**."
      ],
      list: [
        "Pensar que **tener hardware wallet es toda la estrategia de seguridad**.",
        "**Reutilizar contraseñas, dispositivos o direcciones** sin separar niveles de riesgo.",
        "**Firmar sin leer** (aprobaciones, permisos ilimitados, contratos desconocidos).",
        "Confiarse ante phishing bien disfrazado.",
        "Ignorar **actualizaciones críticas de firmware o navegador**."
      ],
      bluepoints: [
        "⚠️ Los atacantes usan tu rutina y tus hábitos, no solo tu tecnología.",
        "🛡️ Cada error tendrá su antídoto explicado en capítulos posteriores."
      ]
    },
    {
      heading: "Mapa rápido del contenido",
      text: [
        "Verás primero un **diagrama del bosque**: identidades, custodia y amenazas.",
        "Luego recorreremos el terreno paso a paso: **billeteras, firmas, llaves, contratos, DeFi, NFT, validaciones y protocolos personales**."
      ],
      bluepoints: [
        "🗺️ Primero entendemos la estructura.",
        "🧱 Luego construimos una base sólida.",
        "🚨 Finalmente, aprendemos a responder ante incidentes."
      ]
    },
    {
      heading: "¿Qué puedes esperar —y qué no?",
      text: [
        "Este libro ofrece **método y criterio**, no promesas mágicas.",
        "No vas a encontrar: ‘**qué moneda comprar**’, ‘**cómo duplicar tus ganancias**’ o ‘**la fórmula secreta del éxito**’.",
        "En su lugar tendrás: **procedimientos, análisis de riesgos, buenas prácticas y métodos para pensar con autonomía**."
      ],
      bluepoints: [
        "❌ No esperes consejos financieros.",
        "✅ Sí espera **herramientas para pensar y decidir**.",
        "📌 El objetivo final: que seas capaz de decir **“lo analicé y sé por qué hago lo que hago.”**"
      ]
    },
    {
      heading: "Punto de partida",
      text: [
        "Ahora tienes el mapa, la brújula y el punto de partida.",
        "Estás lista/o para entrar al **bosque oscuro del blockchain**, no como turista, sino como **explorador consciente**.",
        "**A partir de aquí, comenzamos el recorrido.**"
      ],
      bluepoints: [
        "🔦 El mapa está listo.",
        "🏕️ El siguiente capítulo: **identidad y billeteras**.",
        "🚶‍♀️🚶‍♂️ Empieza la travesía."
      ]
    }
  ]
};

export default CAP1;
